import { useState, useRef, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Calendar from 'react-calendar';
import Stack from 'react-bootstrap/Stack';
import './SingleDatePicker.css';

function formatDate(date) {
  if (!date) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  return d < today;
};

export default function SingleDatePicker({
  value,
  disabled = false,
  holidays = [],
  placeholder = 'Select Date',
  useClickOutside,
  onSelect,
  nonWorkingDays = [],
  readOnly = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const calendarRef = useRef(null);

  useClickOutside(calendarRef, () => setIsOpen(false));

  useEffect(() => {
    if (value) {
      const [d, m, y] = value.split('/');
      setSelectedDate(new Date(`${y}-${m}-${d}`));
    }
  }, [value]);

  const holidaySet = new Set(holidays);

  const getWeekday = (date) => date.toLocaleDateString('en-GB', { weekday: 'long' });

  function getLocalDateString(date) {
    return date.toLocaleDateString('en-CA'); // Outputs YYYY-MM-DD
  }

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const formatted = formatDate(date);
    onSelect(formatted);
    setIsOpen(false);
  };

  return (
    <div className="position-relative">
      <Stack direction="horizontal">
        <Form.Control
          className="single-date-input"
          type="text"
          placeholder={placeholder}
          value={selectedDate ? formatDate(selectedDate) : ''}
          readOnly={readOnly}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(true)}
        />
      </Stack>

      {isOpen && (
        <div ref={calendarRef} className="calendar-popup">
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            prev2Label={null}
            next2Label={null}
            prevLabel="«"
            nextLabel="»"
            tileDisabled={({ date, view }) => {
              if (view !== 'month') return false;

              const dateKey = getLocalDateString(date);
              const weekday = getWeekday(date);

              return isPastDate(date) || holidaySet.has(dateKey) || nonWorkingDays.includes(weekday);
            }}
          />
        </div>
      )}
    </div>
  );
}
